import { getMobileStatus } from 'flow/actions/extension-actions';
import {
    callAndRecordSound,
    createNewSound,
} from 'flow/actions/sounds-actions';
import BaseModal from 'flow/components/ui-components/BaseModal';
import useAppContext from 'flow/hooks/useAppContext';
import { ExtensionKey } from 'flow/types/EntityKeys';

import {
    createSoundInitialState,
    validSoundTypes,
    UpdateFormDataFunction,
} from 'flow/types/CreateSoundTypes';
import { useEffect, useRef, useState } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import CreateSound from '../custom-components/CreateSound';

interface Props {
    extensionKey?: ExtensionKey;
    isPrivate?: boolean;
}

const validFileFormat = (file: File | null): boolean => {
    return !!file && validSoundTypes.includes(file.type);
};

const CreateSoundModal = ({ extensionKey, isPrivate = false }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const [state, setState] = useState(createSoundInitialState);

    const mobileStatus = useAppSelector((state) =>
        state.extensions.getIn(['extensions', extensionKey, 'mobileStatus']),
    );
    const hasFixedNumber = useAppSelector((state) =>
        state.extensions.hasIn([
            'extensions',
            extensionKey,
            'contact',
            'fixed',
            'number',
        ]),
    );

    const dropZoneRef = useRef<HTMLLabelElement | null>(null);
    const fileUploadRef = useRef<HTMLInputElement | null>(null);

    const updateFormData: UpdateFormDataFunction = (updates) => {
        setState((prev) => ({
            ...prev,
            ...updates,
        }));
    };

    useEffect(() => {
        if (extensionKey) {
            dispatch(getMobileStatus(extensionKey));
        }
    }, [extensionKey]);

    const handleCreate = (name: string, description: string, file: File) => {
        const soundDTO = {
            name,
            description,
            privateSound: isPrivate,
        };

        dispatch(createNewSound(soundDTO, file));
    };

    const handleCallAndRecord = (
        name: string,
        description: string,
        callMobile: boolean,
    ) => {
        dispatch(callAndRecordSound(name, description, callMobile, isPrivate));
    };

    const getButtonText = (): string => {
        const buttonTextMap = {
            upload: i18next.t('Recorder.Title'),
            tts: i18next.t('Recorder.Title'),
            'call-landline': i18next.t('Recorder.call.fixed.button'),
            'call-mobile': i18next.t('Recorder.call.mobile.button'),
        };

        return buttonTextMap[state.selectedType];
    };

    const getDisabledState = (): boolean => {
        const disabledStateMap = {
            upload: !state.name || !validFileFormat(state.file),
            tts: !state.name || !validFileFormat(state.file),
            'call-landline': !state.name,
            'call-mobile': !state.name,
        };

        return disabledStateMap[state.selectedType];
    };

    const onSubmit = () => {
        const actionMap = {
            upload: () =>
                handleCreate(state.name, state.description, state.file),
            tts: () => handleCreate(state.name, state.description, state.file),
            'call-landline': () =>
                handleCallAndRecord(state.name, state.description, false),
            'call-mobile': () =>
                handleCallAndRecord(state.name, state.description, true),
        };

        actionMap[state.selectedType]?.();
    };

    return (
        <BaseModal
            open={true}
            header={i18next.t('Recorder.new.sound')}
            disableSubmit={getDisabledState()}
            onSubmit={onSubmit}
            confirmButtonText={getButtonText()}
        >
            <CreateSound
                hasFixed={hasFixedNumber}
                mobileStatus={mobileStatus}
                formData={state}
                updateFormData={updateFormData}
                dropZoneRef={dropZoneRef}
                fileUploadRef={fileUploadRef}
                isPrivate={isPrivate}
            />
        </BaseModal>
    );
};

export default CreateSoundModal;
